/**
 * Verify Code Page Component
 * Allows users to enter the 6-digit verification code sent to their email
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, X, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import customAuthService from '../../services/customAuthService';
import { validateVerificationCode, validatePassword, validatePasswordConfirmation, checkPasswordStrength } from '../../utils/validation';
import { useTranslationNamespace } from '../../i18n/hooks/useTranslationNamespace';
import '../../styles/auth/CustomLoginPage.css';

const VerifyCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslationNamespace('login');
  const email = location.state?.email || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [success, setSuccess] = useState(false);

  // Refs for input fields
  const inputRefs = useRef([]);

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  // Check password strength on change
  useEffect(() => {
    if (formData.newPassword) {
      const strength = checkPasswordStrength(formData.newPassword);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(null);
    }
  }, [formData.newPassword]);

  /**
   * Handle input change for code digits
   */
  const handleChange = (index, e) => {
    let value = e.target.value;

    // If value is longer than 1, take the last character (user typed a new digit)
    if (value.length > 1) {
      value = value.slice(-1);
    }

    // Only allow single digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Clear errors
    if (fieldError) setFieldError('');
    if (error) setError('');

    // Auto-focus next input after a digit is entered
    if (value && index < 5) {
      setTimeout(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }, 10);
    }
  };

  /**
   * Handle key down for backspace and navigation
   */
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Allow arrow keys to navigate between inputs
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * Handle focus - select all text for easy replacement
   */
  const handleFocus = (index) => {
    const input = inputRefs.current[index];
    if (input) {
      requestAnimationFrame(() => {
        input.select();
      });
    }
  };

  /**
   * Handle paste event
   * Always fills from index 0 to ensure full code is entered correctly
   */
  const handlePaste = (e) => {
    console.log(`[OTP Debug] handlePaste called`);
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    console.log(`[OTP Debug] Pasted data: "${pastedData}"`);

    // Accept digits only
    const digitsOnly = pastedData.replace(/\D/g, '');
    console.log(`[OTP Debug] Digits only: "${digitsOnly}"`);

    if (digitsOnly.length > 0) {
      const newCode = [...code];

      // Always fill from index 0 to ensure proper code entry
      for (let i = 0; i < digitsOnly.length && i < 6; i++) {
        newCode[i] = digitsOnly[i];
      }

      setCode(newCode);
      console.log(`[OTP Debug] Updated code array:`, newCode);

      // Focus the next empty input or the last input
      const nextEmptyIndex = newCode.findIndex((digit) => !digit);
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
      console.log(`[OTP Debug] Focusing index ${focusIndex}`);

      requestAnimationFrame(() => {
        const targetInput = inputRefs.current[focusIndex];
        console.log(`[OTP Debug] Target input ref:`, targetInput);
        if (targetInput) {
          targetInput.focus();
          targetInput.select();
          console.log(`[OTP Debug] Successfully focused index ${focusIndex}`);
        }
      });
    }
  };

  /**
   * Handle password field change
   */
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (error) {
      setError('');
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldError('');
    setFieldErrors({});

    const codeString = code.join('');

    // Validate code
    const validation = validateVerificationCode(codeString, t);
    if (!validation.isValid) {
      setFieldError(validation.error);
      return;
    }

    // Validate password
    const passwordValidation = validatePassword(formData.newPassword, {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumber: true,
      requireSpecial: true,
    }, t);

    if (!passwordValidation.isValid) {
      setFieldErrors({ newPassword: passwordValidation.error });
      return;
    }

    // Validate password confirmation
    const confirmValidation = validatePasswordConfirmation(
      formData.newPassword,
      formData.confirmPassword,
      t
    );

    if (!confirmValidation.isValid) {
      setFieldErrors({ confirmPassword: confirmValidation.error });
      return;
    }

    setLoading(true);

    try {
      await customAuthService.verifyResetCode(email, codeString, formData.newPassword);

      // Show success message and redirect to login
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      // Use translation key from API response if available, otherwise fallback to generic message
      const errorMessageKey = err.response?.data?.message;
      setError(errorMessageKey ? t(errorMessageKey) : (err.message || t('failedToResetPassword')));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resend code
   */
  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      await customAuthService.requestPasswordReset(email);
      setError('');
      // Show success message
      alert(t('newCodeSent'));
    } catch (err) {
      // Use translation key from API response if available, otherwise fallback to generic message
      const errorMessageKey = err.response?.data?.message;
      setError(errorMessageKey ? t(errorMessageKey) : (err.message || t('failedToResendCode')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="custom-login-page">
      {/* Header */}
      <header className="custom-login-header">
        <Link to="/" className="custom-login-logo">
          <img
            src="/logo.svg"
            alt="Promotly Logo"
            className="custom-login-logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <span className="custom-login-logo-text">Promotly</span>
        </Link>
      </header>

      {/* Main Container */}
      <div className="custom-login-container">
        {/* Form Section (Centered) */}
        <div className="custom-login-form-section" style={{ gridColumn: '1 / -1' }}>
          <div className="custom-login-form-wrapper">
            {/* Back Button */}
            <Link
              to="/forgot-password"
              className="custom-login-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}
            >
              <ArrowLeft size={16} style={{ transform: document.documentElement.dir === 'rtl' ? 'rotate(180deg)' : 'none' }} />
              {t('back')}
            </Link>

            {/* Title */}
            <h1 className="custom-login-title">{t('enterVerificationCode')}</h1>
            <p className="custom-login-subtitle">
              {t('codeSentTo')} <strong>{email}</strong>
            </p>

            {/* Success Alert */}
            {success && (
              <div
                className="custom-login-error-alert"
                style={{ background: '#d1fae5', borderColor: '#6ee7b7', color: '#065f46' }}
                role="status"
              >
                <span>{t('passwordResetSuccessful')}</span>
              </div>
            )}

            {/* Error Alert */}
            {error && !success && (
              <div className="custom-login-error-alert" role="alert">
                <AlertCircle className="custom-login-error-icon" size={18} />
                <span>{error}</span>
                <button
                  className="custom-login-error-dismiss"
                  onClick={() => setError('')}
                  aria-label={t('close')}
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* Form */}
            <form className="custom-login-form" onSubmit={handleSubmit} noValidate>
              {/* Code Input Fields */}
              <div className="custom-login-form-group">
                <label className="custom-login-label">{t('verificationCode')}</label>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    marginTop: '16px',
                    direction: 'ltr', // Force LTR so cells always fill left-to-right
                  }}
                >
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className={`custom-login-input ${fieldError ? 'error' : ''}`}
                      style={{
                        width: '56px',
                        height: '56px',
                        textAlign: 'center',
                        fontSize: '24px',
                        fontWeight: '600',
                      }}
                      value={digit}
                      onChange={(e) => handleChange(index, e)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      onFocus={() => handleFocus(index)}
                      disabled={loading}
                      autoFocus={index === 0}
                      aria-label={`${t('digit')} ${index + 1}`}
                      aria-invalid={!!fieldError}
                    />
                  ))}
                </div>
                {fieldError && (
                  <span className="custom-login-field-error" role="alert" style={{ textAlign: 'center', display: 'block', marginTop: '12px' }}>
                    {fieldError}
                  </span>
                )}
              </div>

              {/* New Password Field */}
              <div className="custom-login-form-group">
                <label htmlFor="newPassword" className="custom-login-label">
                  {t('newPassword')}
                </label>
                <div className="custom-login-password-wrapper">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    className={`custom-login-input ${fieldErrors.newPassword ? 'error' : ''}`}
                    placeholder={t('newPasswordPlaceholder')}
                    value={formData.newPassword}
                    onChange={handlePasswordChange}
                    disabled={loading}
                    autoComplete="new-password"
                    aria-invalid={!!fieldErrors.newPassword}
                    aria-describedby={fieldErrors.newPassword ? 'newPassword-error' : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="custom-login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('hidePasswordLabel') : t('showPasswordLabel')}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.newPassword && (
                  <span id="newPassword-error" className="custom-login-field-error" role="alert">
                    {fieldErrors.newPassword}
                  </span>
                )}
              </div>

              {/* Password Requirements Checklist */}
              {passwordStrength && (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <p className="custom-login-label" style={{ marginBottom: '12px' }}>
                    {t('passwordRequirements')}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {passwordStrength.requirements.map((req) => (
                      <div
                        key={req.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: req.met ? '#10b981' : '#94a3b8',
                        }}
                      >
                        {req.met ? (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ flexShrink: 0 }}>
                            <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 11-1.06-1.06l7.25-7.25a.75.75 0 011.06 0z" />
                            <path d="M2.22 6.22a.75.75 0 010 1.06l3.5 3.5a.75.75 0 01-1.06 1.06l-3.5-3.5a.75.75 0 011.06-1.06z" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ flexShrink: 0 }}>
                            <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          </svg>
                        )}
                        <span>{req.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirm Password Field */}
              <div className="custom-login-form-group">
                <label htmlFor="confirmPassword" className="custom-login-label">
                  {t('confirmPassword')}
                </label>
                <div className="custom-login-password-wrapper">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`custom-login-input ${fieldErrors.confirmPassword ? 'error' : ''}`}
                    placeholder={t('confirmPasswordPlaceholder')}
                    value={formData.confirmPassword}
                    onChange={handlePasswordChange}
                    disabled={loading}
                    autoComplete="new-password"
                    aria-invalid={!!fieldErrors.confirmPassword}
                    aria-describedby={fieldErrors.confirmPassword ? 'confirmPassword-error' : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="custom-login-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? t('hidePasswordLabel') : t('showPasswordLabel')}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <span id="confirmPassword-error" className="custom-login-field-error" role="alert">
                    {fieldErrors.confirmPassword}
                  </span>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="custom-login-button"
                disabled={loading || success || code.some((d) => !d) || !formData.newPassword || !formData.confirmPassword}
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span className="custom-login-spinner" aria-hidden="true"></span>
                    <span>{t('resetting')}</span>
                  </>
                ) : (
                  <span>{t('resetPassword')}</span>
                )}
              </button>
            </form>

            {/* Resend Code */}
            <div className="custom-login-footer">
              <p style={{ marginTop: '16px', marginBottom: '8px' }}>
                {t('didntReceiveCode')}{' '}
                <button
                  onClick={handleResendCode}
                  className="custom-login-link"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginInlineStart: '8px' }}
                  disabled={loading}
                >
                  {t('resend')}
                </button>
              </p>
              <p style={{ marginTop: '8px', marginBottom: '0' }}>
                {t('rememberPassword')}{' '}
                <Link to="/login" className="custom-login-link" style={{ marginInlineStart: '8px' }}>
                  {t('login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCodePage;